import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Sigla de uma UF precedida da bandeira do estado.
 *
 * As bandeiras são as mesmas de `app-estado-select` — os PNGs em `public/images/estados`, um por
 * sigla. Aqui elas entram por caminho montado a partir da própria sigla, e não por uma tabela de
 * `{ sigla, bandeira }` como lá: o seletor precisa da lista inteira para preencher as opções, esta
 * célula só precisa da UF que a linha traz.
 *
 * É componente, e não marcação repetida nas três abas, pelo mesmo motivo de
 * `app-identificacao-pessoa` e `app-vigencia-solicitacao`: copiado em cada template, divergiria na
 * primeira alteração. E é UM bloco para a tabela e para o cartão, senão a mesma UF se leria de um
 * jeito em cada visão.
 *
 * A imagem é DECORATIVA (`alt=""`): a sigla está escrita ao lado, e um `alt` com a UF faria o leitor
 * de tela dizer "DF DF".
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-bandeira-uf',
    standalone: true,
    templateUrl: './bandeira-uf.html',
    styleUrl: './bandeira-uf.scss'
})
export class BandeiraUf {
    /** UF crua da linha — tolera minúsculas, espaços e o `-` de "não informada". */
    readonly uf = input<string>('');

    /**
     * As siglas que TÊM arquivo em `public/images/estados`.
     *
     * A checagem existe porque o caminho é montado por concatenação: sem ela, um valor inesperado
     * no campo (lixo do legado, país em vez de estado) viraria um `<img>` quebrado — o ícone de
     * imagem partida do navegador é mais ruidoso na tabela do que a ausência da bandeira.
     */
    private static readonly SIGLAS_COM_BANDEIRA = new Set(['BR', 'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']);

    /** A sigla normalizada, ou vazio quando o campo não traz uma UF. */
    readonly sigla = computed(() => {
        const valor = this.uf().trim().toUpperCase();
        return /^[A-Z]{2}$/.test(valor) ? valor : '';
    });

    /** Caminho da bandeira, ou `null` quando não há sigla reconhecida. */
    readonly bandeira = computed(() => {
        const sigla = this.sigla();
        return BandeiraUf.SIGLAS_COM_BANDEIRA.has(sigla) ? `images/estados/${sigla}.png` : null;
    });

    /** O que a célula escreve: a sigla, ou o traço de "não informada". */
    readonly rotulo = computed(() => this.sigla() || '-');
}
